import React, { useEffect, useState } from "react";
import { ArrowLeftIcon } from "../../assets/svgs/icons-svg";
import { KYCFormView } from "./KYCFormView";
import { KYBFormView } from "./KYBFormView";
import { IdentityVerificationProps, KYCForm, KYBForm, VerificationType } from "../../interfaces/bank";
import EstateVerificationService, { EstateVerificationDetails } from "../../api/estate-verification-service";
import EstateService from "../../api/estate-service";

const unwrapRecord = (response: any): EstateVerificationDetails | null => {
  const value = response?.data?.data ?? response?.data ?? response;
  return value && typeof value === "object" ? value : null;
};

const normalizeStatus = (status?: string) => {
  const value = (status ?? "").toLowerCase();
  return ["verified", "approved", "complete", "completed", "successful"].includes(value)
    ? "verified"
    : "pending";
};

const toFormData = (type: VerificationType, form: KYBForm) => {
  const data = new FormData();
  data.append("verificationType", type.toLowerCase());
  Object.entries(form).forEach(([key, value]) => {
    if (value instanceof File) data.append(key, value);
    else if (value !== null && value !== undefined) data.append(key, String(value));
  });
  return data;
};

const toKycPayload = async (form: KYCForm) => {
  const documents = form.idDocument
    ? [{
        code: "id_document",
        label: "Proof of Address",
        url: await EstateService.uploadPhoto(form.idDocument),
        mimeType: form.idDocument.type,
        size: form.idDocument.size,
      }]
    : [];

  return {
    verificationType: "kyc" as const,
    details: {
      fullName: form.fullName,
      dateOfBirth: form.dateOfBirth,
      idType: form.idType,
      idNumber: form.idNumber,
      idExpiryDate: form.idExpiryDate,
    },
    documents,
  };
};

const IdentityVerification: React.FC<IdentityVerificationProps> = ({ estateId, onBack, onSave, onStatusChange, defaultType = "KYC" }) => {
  const [activeType, setActiveType] = useState<VerificationType>(defaultType);
  const [record, setRecord] = useState<EstateVerificationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    EstateVerificationService.getVerification(estateId)
      .then((response) => {
        if (!mounted) return;
        const existing = unwrapRecord(response);
        if (!existing) return;
        setRecord(existing);
        const existingType = existing.verificationType?.toUpperCase();
        if (existingType === "KYC" || existingType === "KYB") setActiveType(existingType);
        onStatusChange?.(existing.status);
      })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [estateId, onStatusChange]);

  const handleSubmit = async (type: VerificationType, form: KYCForm | KYBForm) => {
    setSaving(true);
    setError("");
    try {
      const payload = type === "KYC"
        ? await toKycPayload(form as KYCForm)
        : toFormData(type, form as KYBForm);
      const response = record
        ? await EstateVerificationService.updateVerification(estateId, payload)
        : await EstateVerificationService.submitVerification(estateId, payload);
      const saved = unwrapRecord(response) ?? { verificationType: type, status: "pending" };
      setRecord(saved);
      onSave(type, saved.status);
    } catch (err: any) {
      setError(err?.message ?? "Could not save verification details. Please try again.");
    } finally { setSaving(false); }
  };

  const status = normalizeStatus(record?.status);

  return (
    <div className="w-full">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-8 group">
        <span className="group-hover:-translate-x-0.5 transition-transform"><ArrowLeftIcon /></span> Back
      </button>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Verification</h2>
        <p className="text-sm text-gray-500 mt-1">Complete verification to enable resident levy payments.</p>
        {status === "verified" && record && <p className="text-sm text-green-600 mt-2">Your identity verification is complete.</p>}
        {status === "pending" && record && <p className="text-sm text-amber-600 mt-2">Your verification is in progress.</p>}
      </div>
      {loading ? <p className="text-sm text-gray-500">Checking verification status...</p> : (
        <>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit mb-8">
            {(["KYC", "KYB"] as VerificationType[]).map((type) => (
              <button key={type} onClick={() => setActiveType(type)} className={`px-5 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${activeType === type ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>{type}</button>
            ))}
          </div>
          {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
          {saving && <p className="text-sm text-gray-500 mb-4">Saving verification details...</p>}
          {activeType === "KYC" && <KYCFormView initialValues={record?.verificationType?.toUpperCase() === "KYC" ? record.details as Partial<KYCForm> : undefined} onSave={(form) => handleSubmit("KYC", form)} />}
          {activeType === "KYB" && <KYBFormView initialValues={record?.verificationType?.toUpperCase() === "KYB" ? record.details as Partial<KYBForm> : undefined} onSave={(form) => handleSubmit("KYB", form)} />}
        </>
      )}
    </div>
  );
};

export default IdentityVerification;
